import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {isCoordinateOutOfRangeOfOtherCoordinate} from '@application/helper/production-schedule-builder';
import {cloneProductionScheduleElement} from '@application/helper/production-schedule-builder/clone-production-schedule-element';
import {getClosestProductionScheduleItemAndIndexRelativeToMouseCoordinates} from '@application/helper/production-schedule-builder/get-closest-production-schedule-item-and-index-relative-to-mouse-coordinates';
import {getMouseCoordinatesInProductionScheduleBuilderGroup} from '@application/helper/production-schedule-builder/get-mouse-coordinates-in-production-schedule-builder-group';
import {getUnderlyingElement} from '@application/helper/production-schedule-builder/get-underlying-element';
import {isInLowerHalfOfProductionScheduleItem} from '@application/helper/production-schedule-builder/is-in-lower-half-of-production-schedule-item';
import {DraggingService} from '@application/services/dragging/dragging.service';
import {Coordinates} from '@domain/coordinates';
import {PositionOfDialog} from '@domain/position-of-dialog';
import {BmpProductionScheduleItemInPathGroup} from '@domain/production-schedule/bmp-production-schedule-item-in-path-group';
import {ClosestProductionScheduleItemInPathGroup} from '@domain/production-schedule/closest-production-schedule-item-in-path-group';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionScheduleOrderLine} from '@domain/production-schedule/production-schedule-order-line';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {ListDrawing} from '@presentation/components/drawing-list/list-drawing';
import {RepositionDialogComponent} from '@presentation/components/reposition-dialog/reposition-dialog.component';
import {ProductionScheduleBuilderAspectRatio} from '@presentation/pages/texfab/production-schedule/add/plan/builder/production-schedule-builder-aspect-ratio';
import {EditProductionSchedulePathOfColoredYarnSetComponent} from '@presentation/pages/texfab/production-schedule/add/plan/modals/edit-production-schedule-path-of-colored-yarn-set/edit-production-schedule-path-of-colored-yarn-set.component';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {AlertDialogResult, AssertionUtils, BaseComponent, convertToCommercialUnitCentimeter, DialogBuilderFactoryService, DialogType, TranslateService, Unit} from '@vdw/angular-component-library';
import {drag as d3Drag, D3DragEvent} from 'd3-drag';
import {BaseType, ContainerElement, pointer as d3Pointer, select as d3Select, selectAll as d3SelectAll, Selection as d3Selection} from 'd3-selection';
import {cloneDeep, first, indexOf, inRange, isEmpty, isEqual, isNil, isUndefined, last, multiply, size, sum, sumBy} from 'lodash-es';
import {takeUntil} from 'rxjs/operators';
import {PathItemInfoTooltipContext} from '../../../modals/path-item-info-tooltip/path-item-info-tooltip-context.enum';
import {PathItemInfoTooltipComponent} from '../../../modals/path-item-info-tooltip/path-item-info-tooltip.component';
import {ProductionScheduleItemInPathGroupContainerComponent} from './production-schedule-item-in-path-group-container/production-schedule-item-in-path-group-container.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-production-schedule-path-of-colored-yarn-set-new]',
  templateUrl: './production-schedule-path-of-colored-yarn-set-new.component.html',
  styleUrls: ['./production-schedule-path-of-colored-yarn-set-new.component.scss']
})
export class ProductionSchedulePathOfColoredYarnSetNewComponent extends BaseComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private static isDraggingProductionSchedulePath: boolean;
  @Input() public productionSchedulePath: ProductionSchedulePath;
  @Input() public productionSchedulePathIndex: number;
  @Input() public productionScheduleBuilderHeight: number;
  @Input() public commercialUnit: Unit;
  @Input() public productionScheduleColoredYarnSetIndex: number;
  @Input() public productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  @Input() public productionSchedulePhase: ProductionSchedulePhase;
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public aspectRatio: ProductionScheduleBuilderAspectRatio;
  @Input() public isEditPathsEnabled: boolean;
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Input() public isProductionScheduleDisplayOverview: boolean;
  @Output() public hoveredProductionSchedulePathComponent: EventEmitter<ProductionSchedulePathOfColoredYarnSetNewComponent> = new EventEmitter<ProductionSchedulePathOfColoredYarnSetNewComponent>();
  @Output() public productionSchedulePlanChanged: EventEmitter<void> = new EventEmitter<void>();
  @ViewChildren('itemsInPathGroup') public itemInPathGroupComponents: QueryList<ProductionScheduleItemInPathGroupContainerComponent>;

  public readonly productionSchedulePathSeparatorWidth = 1;

  private mouseCoordinatesOfDragStart: Coordinates;
  private destinationOfDraggedProductionSchedulePath: d3Selection<BaseType, OverviewListColoredYarnSetWithStartDent | ProductionSchedulePath, BaseType, undefined>;
  private listenerMouseEnter: () => void;
  private listenerMouseLeave: () => void;
  private listenerMouseClick: () => void;
  private dragInitializedOnCurrentProductionSchedulePath: boolean = false;
  private canOpenHoverInfoTooltip: boolean = false;
  private currentSubscription: Subscription;

  private readonly labelForProductionScheduleBuilderGroup = 'production-schedule-builder-group';
  private readonly labelForProductionScheduleColoredYarnSet = 'production-schedule-colored-yarn-set';
  private readonly labelForProductionSchedulePath = 'production-schedule-path-of-colored-yarn-set';
  private readonly dropIndicatorPositionHeight = 8;
  private dialogRef: MatDialogRef<RepositionDialogComponent>;
  private readonly DIALOG_WIDTH = 334;
  private readonly REPOSITION_DIALOG_CLASSES = ['reposition-dialog', 'reposition-dialog-colored-yarn-set'];

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly zone: NgZone,
    private readonly draggingService: DraggingService
  ) {
    super();
  }

  @HostListener('dragleave', ['$event'])
  public onDragLeaveProductionSchedulePath(event: any): void {
    event.stopPropagation();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.initialiseProductionSchedulePathOfColoredYarnSetComponent();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('aspectRatio' in changes || 'productionSchedule' in changes || 'productionScheduleBuilderHeight' in changes) {
      this.setProductionSchedulePathOfColoredYarnSetAttributes();
    }
  }

  public ngAfterViewInit(): void {
    this.setProductionSchedulePathOfColoredYarnSetEventHandlers();

    setTimeout(() => {
      this.canOpenHoverInfoTooltip = true;
    }, 1000);
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();

    if (!isUndefined(this.listenerMouseEnter)) {
      this.listenerMouseEnter();
    }
    if (!isUndefined(this.listenerMouseLeave)) {
      this.listenerMouseLeave();
    }
    if (!isUndefined(this.listenerMouseClick)) {
      this.listenerMouseClick();
    }
    this.dialogRef?.close();
  }

  public getProductionSchedulePathOfColoredYarnSetWidth(): number {
    return multiply(this.productionSchedulePath.technicalWidthInDents, this.aspectRatio.width);
  }

  public onProductionSchedulePlanChanged(): void {
    this.productionSchedulePlanChanged.emit();
  }

  public onClickProductionSchedulePathEditOverlay(event: MouseEvent): void {
    if (this.isEditPathsEnabled) {
      const parentElement: Element = (event.target as Element).parentElement;

      this.renderer.addClass(parentElement, 'active');

      this.dialogBuilderFactoryService
        .getBuilder()
        .withClass('edit-drawing-in-path')
        .withWidth('268px')
        .openDialog(EditProductionSchedulePathOfColoredYarnSetComponent, {
          matDialogPosition: {
            y: event.clientY,
            x: event.clientX
          }
        })
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((deleteProductionSchedulePathOfColoredYarnSet: boolean) => {
          if (deleteProductionSchedulePathOfColoredYarnSet) {
            this.deleteProductionSchedulePath();
          }

          this.renderer.removeClass(parentElement, 'active');
        });
    }
  }

  public getProductionSchedulePathLeft(): number {
    return this.elementRef.nativeElement.x.baseVal.value;
  }

  public getProductionSchedulePathWidth(): number {
    return Math.max(
      ...this.itemInPathGroupComponents.toArray().map((itemInPathGroupComponent: ProductionScheduleItemInPathGroupContainerComponent) => {
        return itemInPathGroupComponent.drawingWidth;
      })
    );
  }

  public getProductionSchedulePathTop(): number {
    return Math.min(
      ...this.itemInPathGroupComponents.toArray().map((itemInPathGroupComponent: ProductionScheduleItemInPathGroupContainerComponent) => {
        return itemInPathGroupComponent.elementRef.nativeElement.y.baseVal.value;
      })
    );
  }

  public getProductionSchedulePathSeparatorHorizontalPosition(): number {
    return this.getProductionSchedulePathOfColoredYarnSetWidth() - this.productionSchedulePathSeparatorWidth;
  }

  public hasPermissionToNewVerifyStep(): boolean {
    return this.currentSubscription?.hasPermission(Permission.TEXFAB_VERIFY_NEW);
  }

  public openHoverInfoTooltip(event: MouseEvent, productionScheduleItem: ProductionScheduleItemInPathGroup, index: number): void {
    if (!productionScheduleItem.canBeRecolored() || !this.canOpenHoverInfoTooltip) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.dialogRef?.close();
      this.dialogRef = this.dialogBuilderFactoryService
        .getBuilder()
        .withWidth(`${this.DIALOG_WIDTH}px`)
        .withClass(this.REPOSITION_DIALOG_CLASSES)
        .withCustomBackdrop('no-pointer-events-backdrop')
        .open(RepositionDialogComponent, {
          showArrow: false,
          event,
          sourceElement: event.target,
          component: PathItemInfoTooltipComponent,
          positionOfDialog: PositionOfDialog.LEFT,
          verticalOffset: 0,
          horizontalOffset: 0,
          moveWithCursor: false,
          context: PathItemInfoTooltipContext.PATH_DESIGN,
          number: index + 1,
          drawingName: productionScheduleItem.drawing?.name,
          image: productionScheduleItem.scheduleItemType === DrawingType.MANUAL_NON_PRODUCTION_ITEM ? {data: 'assets/images/jute-background.svg'} : productionScheduleItem.drawing?.image,
          finishingName: this.getFinishingName(productionScheduleItem),
          cutbarBefore: productionScheduleItem.getCutbarInformation()?.cutbarBefore?.commercialLengthInMM,
          cutbarAfter: productionScheduleItem.getCutbarInformation()?.cutbarAfter?.commercialLengthInMM,
          cutbarBeforeInPicks: productionScheduleItem.getCutbarInformation()?.cutbarBefore?.technicalLengthInNumberOfPicks,
          cutbarAfterInPicks: productionScheduleItem.getCutbarInformation()?.cutbarAfter?.technicalLengthInNumberOfPicks,
          numberOfPieces: this.productionSchedule.machine.jacquard.isSisal
            ? `${productionScheduleItem.quantity}`
            : `${productionScheduleItem.quantity}x${this.productionSchedule.getProductionScheduleItemMultiplier()}`,
          quantity: this.productionSchedule.machine.jacquard.isSisal
            ? productionScheduleItem.quantity
            : productionScheduleItem.quantity * this.productionSchedule.getProductionScheduleItemMultiplier(),
          quality: this.productionSchedule.machineQuality.technicalName,
          orderline: this.getOrderlineProperty(productionScheduleItem.orderLineId)?.salesOrderLineReference ?? '',
          sales: this.getOrderlineProperty(productionScheduleItem.orderLineId)?.salesOrderReference ?? '',
          customer: this.getOrderlineProperty(productionScheduleItem.orderLineId)?.customerReference ?? '',
          dueDate: this.getOrderlineProperty(productionScheduleItem.orderLineId)?.desiredDate,
          width: convertToCommercialUnitCentimeter({
            unit: Unit.MILLIMETER,
            value: productionScheduleItem?.commercialDimensionsInMM?.widthInMM
          }),
          length: convertToCommercialUnitCentimeter({
            unit: Unit.MILLIMETER,
            value: productionScheduleItem?.commercialDimensionsInMM?.heightInMM
          }),
          widthInDents: productionScheduleItem.technicalDimensions.widthInDents,
          heightInPicks: productionScheduleItem.technicalDimensions.heightInPicks,
          isSisal: this.productionSchedule.machine.jacquard.isSisal,
          coloredYarnSet: this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet,
          creelMapping: this.productionSchedule.mappingForCurrentPositionOnMachine ? this.productionSchedule.mappingForCurrentPositionOnMachine[this.productionScheduleColoredYarnSetIndex] : null,
          mainColorPosition: productionScheduleItem.mainColorPosition,
          borderColorPosition: productionScheduleItem.borderColorPosition,
          shape: productionScheduleItem.drawing.shape
        });
    });
  }

  public closeHoverInfoTooltip(): void {
    this.dialogRef?.close();
  }

  private isMousePositionInsideProductionSchedulePath(event: DragEvent, destinationOfDraggedProductionScheduleItem: d3Selection<BaseType, ProductionSchedulePath, BaseType, undefined>): boolean {
    const productionSchedulePathPositionStart = 0;
    const productionSchedulePathPositionEnd = Number(destinationOfDraggedProductionScheduleItem.attr('width'));

    const mouseCoordinatesInProductionSchedulePlanView: Coordinates = this.getMouseCoordinatesInProductionSchedulePath(event);

    const productionSchedulePathDropThreshold = multiply(30, this.aspectRatio.width);

    return inRange(
      mouseCoordinatesInProductionSchedulePlanView.x,
      productionSchedulePathPositionStart + productionSchedulePathDropThreshold,
      productionSchedulePathPositionEnd - productionSchedulePathDropThreshold
    );
  }

  private isMousePositionInLeftSideOfPath(event: DragEvent): boolean {
    const mouseCoordinatesInProductionSchedulePlanView: Coordinates = this.getMouseCoordinatesInProductionSchedulePath(event);

    const productionSchedulePathDropThreshold = multiply(30, this.aspectRatio.width);

    return inRange(mouseCoordinatesInProductionSchedulePlanView.x, 0, productionSchedulePathDropThreshold);
  }

  private droppedNewDrawingOnProductionSchedulePath(event: DragEvent): void {
    if (!this.isEditPathsEnabled && !isNil(this.draggingService.getCurrentData()) && !('quantity' in this.draggingService.getCurrentData())) {
      const draggedItem: ListDrawing = cloneDeep(this.draggingService.getCurrentData());

      const destinationOfDraggedProductionScheduleItem = d3Select(this.elementRef.nativeElement) as d3Selection<BaseType, ProductionSchedulePath, BaseType, undefined>;

      if (!isNil(destinationOfDraggedProductionScheduleItem)) {
        this.addDroppedDrawingToProductionSchedulePath(event, draggedItem, destinationOfDraggedProductionScheduleItem);
      }

      this.renderer.removeClass(first(this.elementRef.nativeElement.children), 'highlight');
      d3Select('.drawing-drag-preview').remove();

      this.removeDropIndicator();
    }
  }

  private addDroppedDrawingToProductionSchedulePath(
    event: DragEvent,
    draggedItem: ListDrawing,
    destinationOfDraggedProductionScheduleItem: d3Selection<BaseType, ProductionSchedulePath, BaseType, undefined>
  ): void {
    if (this.isMousePositionInsideProductionSchedulePath(event, destinationOfDraggedProductionScheduleItem)) {
      const isDraggingOverProductionSchedulePath: boolean = d3Select(document.elementFromPoint(event.clientX, event.clientY)).classed('production-schedule-path-background');

      if (
        isDraggingOverProductionSchedulePath &&
        this.productionSchedule.canAddProductionScheduleItemToProductionSchedulePath(
          draggedItem,
          this.productionSchedulePath.pathNumber,
          this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet
        )
      ) {
        const productionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(
          this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
        );
        const productionScheduleItemsInPathGroup: SVGGraphicsElement[] = this.itemInPathGroupComponents
          .toArray()
          .map((productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroupContainerComponent) => productionScheduleItemInPathGroup.elementRef.nativeElement) as SVGGraphicsElement[];

        const closestProductionScheduleItemInPathGroup: ClosestProductionScheduleItemInPathGroup = getClosestProductionScheduleItemAndIndexRelativeToMouseCoordinates(
          this.getMouseCoordinatesInProductionSchedulePath(event),
          productionScheduleItemsInPathGroup
        );
        let destinationIndexInProductionSchedulePath: number = closestProductionScheduleItemInPathGroup.index;
        const closestProductionScheduleItemInPathGroupComponent: SVGGraphicsElement = closestProductionScheduleItemInPathGroup.element;

        if (
          !isUndefined(closestProductionScheduleItemInPathGroupComponent) &&
          !isInLowerHalfOfProductionScheduleItem(this.getMouseCoordinatesInProductionSchedulePath(event), closestProductionScheduleItemInPathGroupComponent)
        ) {
          destinationIndexInProductionSchedulePath = destinationIndexInProductionSchedulePath + 1;
        }

        this.productionSchedulePlan.addDrawingToProductionSchedulePath(
          draggedItem,
          this.productionSchedulePath.pathNumber,
          productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet,
          !isNil(destinationIndexInProductionSchedulePath) ? destinationIndexInProductionSchedulePath : null
        );
      } else if (
        !this.productionSchedule.enoughWidthToAddProductionScheduleItemToProductionSchedulePath(
          draggedItem,
          this.productionSchedulePath.pathNumber,
          this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet
        )
      ) {
        this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
          titleText: 'PRODUCTION_ORDER.WEAVE_PLAN.WEAVE_PLAN',
          messageText: 'PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.NOT_ENOUGH_WIDTH',
          type: DialogType.INFORMATION
        });
      }
    } else if (this.productionSchedule.canAddProductionScheduleItemToColoredYarnSet(this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent, draggedItem)) {
      const isInLeftSideOfPath: boolean = this.isMousePositionInLeftSideOfPath(event);
      const productionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(
        this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
      );
      this.productionSchedulePlan.addDrawingToColoredYarnSet(
        draggedItem,
        this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent,
        isInLeftSideOfPath
          ? indexOf(productionSchedulePathsOfColoredYarnSet.productionSchedulePaths, this.productionSchedulePath)
          : indexOf(productionSchedulePathsOfColoredYarnSet.productionSchedulePaths, this.productionSchedulePath) + 1
      );
    }
  }

  private initialiseProductionSchedulePathOfColoredYarnSetComponent(): void {
    d3Select(this.elementRef.nativeElement).datum(this.productionSchedulePath);
    this.zone.runOutsideAngular(() => {
      d3Select(this.elementRef.nativeElement)
        .on('drop', (event: DragEvent) => this.droppedNewDrawingOnProductionSchedulePath(event))
        .on('dragover', (event: DragEvent) => this.draggedNewDrawingOverProductionSchedulePath(event))
        .on('dragenter', (event: DragEvent) => event.stopPropagation())
        .on('dragleave', (event: DragEvent) => {
          event.stopPropagation();
          this.removeDropIndicator();
        });
    });
  }

  private getFinishingName(productionScheduleItem: ProductionScheduleItemInPathGroup): string {
    let name = '';

    if (!AssertionUtils.isNullOrUndefined(productionScheduleItem.orderline?.article?.finishing?.nameWithVersion)) {
      name = productionScheduleItem.orderline.article.finishing.nameWithVersion;
    } else if (!AssertionUtils.isNullOrUndefined(productionScheduleItem.drawing) && productionScheduleItem.drawing.drawingType === DrawingType.BMP) {
      const bmpProductionScheduleItem = productionScheduleItem as any as BmpProductionScheduleItemInPathGroup;
      if (!AssertionUtils.isNullOrUndefined(bmpProductionScheduleItem?.finishing?.nameWithVersion)) {
        name = bmpProductionScheduleItem.finishing.nameWithVersion;
      }
    }

    return name;
  }

  private draggedNewDrawingOverProductionSchedulePath(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const draggedItem: ListDrawing = this.draggingService.getCurrentData();

    if (!this.isEditPathsEnabled && !isNil(draggedItem) && !('quantity' in draggedItem)) {
      this.removeDropIndicator();
      const destinationOfDraggedProductionScheduleItem: d3Selection<BaseType, any, BaseType, undefined> = d3Select(this.elementRef.nativeElement);
      const hasDraggedInsideProductionSchedulePath: boolean = this.isMousePositionInsideProductionSchedulePath(event, destinationOfDraggedProductionScheduleItem);

      if (
        hasDraggedInsideProductionSchedulePath &&
        this.productionSchedule.canAddProductionScheduleItemToProductionSchedulePath(
          draggedItem,
          this.productionSchedulePath.pathNumber,
          this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet
        )
      ) {
        this.drawProductionScheduleItemInPathGroupDropIndicator(
          d3Select(this.elementRef.nativeElement).node(),
          Number(last(this.itemInPathGroupComponents.toArray()).elementRef.nativeElement.getAttribute('y')) - this.dropIndicatorPositionHeight
        );
      } else if (
        !hasDraggedInsideProductionSchedulePath &&
        this.productionSchedule.canAddProductionScheduleItemToColoredYarnSet(this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent, draggedItem)
      ) {
        let dropIndicatorPosition = Number(destinationOfDraggedProductionScheduleItem.attr('x'));

        if (!this.isMousePositionInLeftSideOfPath(event)) {
          dropIndicatorPosition += Number(destinationOfDraggedProductionScheduleItem.attr('width'));
        }

        this.drawProductionSchedulePathDropIndicator((destinationOfDraggedProductionScheduleItem.node() as SVGGraphicsElement).parentElement, dropIndicatorPosition);
      }
    }
  }

  private removeDropIndicator(): void {
    d3SelectAll('.production-schedule-drop-indicator').remove();
  }

  private setProductionSchedulePathOfColoredYarnSetAttributes(): void {
    const productionSchedulePathOfColoredYarnSetElement = this.elementRef.nativeElement;

    this.renderer.setAttribute(productionSchedulePathOfColoredYarnSetElement, 'x', `${this.getProductionSchedulePathPosition()}`);
    this.renderer.setAttribute(productionSchedulePathOfColoredYarnSetElement, 'width', `${this.getProductionSchedulePathOfColoredYarnSetWidth()}`);
  }

  private setProductionSchedulePathOfColoredYarnSetEventHandlers(): void {
    const productionSchedulePathInColoredYarnSetElement = this.elementRef.nativeElement;

    this.listenerMouseEnter = this.renderer.listen(productionSchedulePathInColoredYarnSetElement, 'mouseenter', () => this.onMouseEnter());
    this.listenerMouseLeave = this.renderer.listen(productionSchedulePathInColoredYarnSetElement, 'mouseleave', () => this.onMouseLeave());

    d3Select(productionSchedulePathInColoredYarnSetElement).call(
      d3Drag()
        .on('start', (event: D3DragEvent<SVGGraphicsElement, ProductionSchedulePath[], ProductionSchedulePath>) => this.onDragStart(event.sourceEvent))
        .on('drag', (event: D3DragEvent<SVGGraphicsElement, ProductionSchedulePath[], ProductionSchedulePath>) => this.onDrag(event.sourceEvent))
        .on('end', (event: D3DragEvent<SVGGraphicsElement, ProductionSchedulePath[], ProductionSchedulePath>) => this.onDragEndProductionSchedulePath(event.sourceEvent))
    );
  }

  private onMouseEnter(): void {
    if (!ProductionSchedulePathOfColoredYarnSetNewComponent.isDraggingProductionSchedulePath && !ProductionScheduleItemInPathGroupContainerComponent.isDraggingProductionScheduleItemInPathGroup) {
      this.hoveredProductionSchedulePathComponent.emit(this);
    }
  }

  private onMouseLeave(): void {
    if (!ProductionSchedulePathOfColoredYarnSetNewComponent.isDraggingProductionSchedulePath && !ProductionScheduleItemInPathGroupContainerComponent.isDraggingProductionScheduleItemInPathGroup) {
      this.hoveredProductionSchedulePathComponent.emit(null);
    }
  }

  private onDragStart(event: DragEvent): void {
    this.mouseCoordinatesOfDragStart = getMouseCoordinatesInProductionScheduleBuilderGroup(event);
  }

  private onDrag(event: DragEvent): void {
    if (this.isEditPathsEnabled) {
      const {x: mouseCoordinateX, y: mouseCoordinateY} = this.getMouseCoordinatesInProductionSchedulePlanView(event);

      const hasDraggedFarEnoughFromStartingPoint: boolean = isCoordinateOutOfRangeOfOtherCoordinate(
        {
          x: mouseCoordinateX,
          y: mouseCoordinateY
        },
        this.mouseCoordinatesOfDragStart
      );
      if (hasDraggedFarEnoughFromStartingPoint || this.dragInitializedOnCurrentProductionSchedulePath) {
        if (this.dragInitializedOnCurrentProductionSchedulePath) {
          const productionSchedulePathSelection = d3Select(this.elementRef.nativeElement);

          productionSchedulePathSelection.attr('x', mouseCoordinateX).attr('y', mouseCoordinateY);

          this.onDragProductionSchedulePath(event);
        } else {
          this.onDragInitializeProductionSchedulePath(event);
        }
      }
    }
  }

  private onDragInitializeProductionSchedulePath(event: DragEvent): void {
    this.dragInitializedOnCurrentProductionSchedulePath = true;
    this.mouseCoordinatesOfDragStart = getMouseCoordinatesInProductionScheduleBuilderGroup(event);
    const productionScheduleWrapperElement = d3Select(`.${this.labelForProductionScheduleBuilderGroup}`).node() as ContainerElement;
    const [mouseCoordinateX] = d3Pointer(event, productionScheduleWrapperElement);
    cloneProductionScheduleElement(this.elementRef, mouseCoordinateX);
    ProductionSchedulePathOfColoredYarnSetNewComponent.isDraggingProductionSchedulePath = true;
  }

  private onDragProductionSchedulePath(event: DragEvent): void {
    this.removeDropIndicator();
    d3SelectAll('.background').classed('highlight', false);

    const destinationOfDraggedProductionSchedulePath = getUnderlyingElement({x: event.x, y: event.y}, `.${this.labelForProductionScheduleColoredYarnSet}, .${this.labelForProductionSchedulePath}`);

    if (!isNil(destinationOfDraggedProductionSchedulePath)) {
      const destinationHasChanged = !isEqual(this.destinationOfDraggedProductionSchedulePath, destinationOfDraggedProductionSchedulePath);
      if (destinationHasChanged) {
        this.destinationOfDraggedProductionSchedulePath = destinationOfDraggedProductionSchedulePath;
      }

      const destinationOfDraggedProductionSchedulePathElement: SVGGraphicsElement = this.destinationOfDraggedProductionSchedulePath.node() as SVGGraphicsElement;
      const destinationOfDraggedProductionSchedulePathClassList = destinationOfDraggedProductionSchedulePathElement.classList;

      if (destinationOfDraggedProductionSchedulePathClassList.contains(this.labelForProductionSchedulePath)) {
        this.handleDragOverProductionSchedulePath(event, destinationOfDraggedProductionSchedulePathElement);
      } else if (destinationOfDraggedProductionSchedulePathClassList.contains(this.labelForProductionScheduleColoredYarnSet)) {
        this.handleDragOverProductionScheduleColoredYarnSet(destinationOfDraggedProductionSchedulePathElement);
      }
    }
  }

  private handleDragOverProductionScheduleColoredYarnSet(destinationOfDraggedProductionSchedulePathElement: SVGGraphicsElement): void {
    const destinationOfDraggedProductionSchedulePath: OverviewListColoredYarnSetWithStartDent = this.destinationOfDraggedProductionSchedulePath.datum() as OverviewListColoredYarnSetWithStartDent;

    if (
      this.productionSchedule.canMoveProductionSchedulePathToColoredYarnSet(
        this.productionSchedulePath.pathNumber,
        this.productionSchedule.getProductionScheduleColoredYarnSetForProductionSchedulePath(this.productionSchedulePath).startDent,
        destinationOfDraggedProductionSchedulePath.startDent
      )
    ) {
      const productionSchedulePaths = d3Select(destinationOfDraggedProductionSchedulePathElement).selectAll(`.${this.labelForProductionSchedulePath}`).nodes();

      const lastProductionSchedulePathSelection = d3Select(last(productionSchedulePaths));
      const dropIndicatorPosition = isEmpty(productionSchedulePaths) ? 0 : sum([Number(lastProductionSchedulePathSelection.attr('x')), Number(lastProductionSchedulePathSelection.attr('width'))]);

      this.drawProductionSchedulePathDropIndicator(destinationOfDraggedProductionSchedulePathElement, dropIndicatorPosition);
    }
  }

  private handleDragOverProductionSchedulePath(event: DragEvent, destinationOfDraggedProductionSchedulePathElement: SVGGraphicsElement): void {
    let destinationOfDraggedProductionSchedulePath: ProductionSchedulePath = this.destinationOfDraggedProductionSchedulePath.datum() as ProductionSchedulePath;

    if (isUndefined(destinationOfDraggedProductionSchedulePath)) {
      destinationOfDraggedProductionSchedulePath = this.productionSchedulePath;
    }

    if (
      this.productionSchedule.canMoveProductionSchedulePathToColoredYarnSet(
        this.productionSchedulePath.pathNumber,
        this.productionSchedule.getProductionScheduleColoredYarnSetForProductionSchedulePath(this.productionSchedulePath).startDent,
        this.productionSchedule.getProductionScheduleColoredYarnSetForProductionSchedulePath(destinationOfDraggedProductionSchedulePath).startDent
      )
    ) {
      const targetPositionOfDraggedProductionSchedulePathInColoredYarnSetPath: number = this.getTargetPositionOfDraggedProductionSchedulePathInColoredYarnSetPath(
        event,
        destinationOfDraggedProductionSchedulePathElement
      );
      this.drawProductionSchedulePathDropIndicator(destinationOfDraggedProductionSchedulePathElement.parentElement, targetPositionOfDraggedProductionSchedulePathInColoredYarnSetPath);
    }
  }

  private cleanProductionSchedulePlanAfterDrop(): void {
    this.removeDropIndicator();
    this.restoreProductionSchedulePathClone();
    this.restoreProductionScheduleToNonDraggingState();
  }

  private restoreProductionSchedulePathClone(): void {
    const draggedProductionSchedulePath = d3SelectAll('.dragged-production-schedule-element');

    const draggedProductionSchedulePathNode = draggedProductionSchedulePath.node() as SVGGraphicsElement;
    if (isNil(draggedProductionSchedulePathNode)) {
      d3Select('.production-schedule-element-clone').remove();
    } else {
      const productionScheduleColoredYarnSetOfDraggedProductionSchedulePath = draggedProductionSchedulePathNode.parentNode;

      const cloneOfDraggedProductionSchedulePath = d3Select('.production-schedule-element-clone')
        .attr('x', draggedProductionSchedulePath.attr('x'))
        .attr('y', draggedProductionSchedulePath.attr('y'))
        .attr('class', this.labelForProductionSchedulePath);

      productionScheduleColoredYarnSetOfDraggedProductionSchedulePath.insertBefore(
        cloneOfDraggedProductionSchedulePath.remove().node() as SVGGraphicsElement,
        (draggedProductionSchedulePath.node() as SVGGraphicsElement).nextSibling
      );
      draggedProductionSchedulePath.remove();
    }
  }

  private restoreProductionScheduleToNonDraggingState(): void {
    this.dragInitializedOnCurrentProductionSchedulePath = false;
    ProductionSchedulePathOfColoredYarnSetNewComponent.isDraggingProductionSchedulePath = false;
  }

  private getTargetIndexOfDestinationProductionSchedulePath(event: DragEvent, destinationOfDraggedProductionSchedulePathElement: SVGGraphicsElement): number {
    const mouseCoordinatesInProductionSchedulePlanView: Coordinates = this.getMouseCoordinatesInProductionSchedulePlanView(event);
    const destinationColoredYarnSetPathElement = destinationOfDraggedProductionSchedulePathElement.parentElement;
    const productionSchedulePathElementsInColoredYarnSet: SVGGraphicsElement[] = this.getProductionSchedulePathElementsInColoredYarnSet(destinationColoredYarnSetPathElement);
    const indexOfDestinationProductionSchedulePath: number = indexOf(productionSchedulePathElementsInColoredYarnSet, destinationOfDraggedProductionSchedulePathElement);

    const destinationPathElementSelection = d3Select(destinationOfDraggedProductionSchedulePathElement);
    const productionSchedulePathPositionStart = Number(destinationPathElementSelection.attr('x'));
    const productionSchedulePathPositionEnd = sum([productionSchedulePathPositionStart, Number(destinationPathElementSelection.attr('width'))]);
    const productionSchedulePathDropThreshold = (productionSchedulePathPositionEnd - productionSchedulePathPositionStart) / 2;

    const isInLeftSideOfPath: boolean = inRange(
      mouseCoordinatesInProductionSchedulePlanView.x,
      productionSchedulePathPositionStart,
      productionSchedulePathPositionStart + productionSchedulePathDropThreshold
    );

    return isInLeftSideOfPath ? indexOfDestinationProductionSchedulePath : indexOfDestinationProductionSchedulePath + 1;
  }

  private getProductionSchedulePathElementsInColoredYarnSet(destinationColoredYarnSetElement: HTMLElement): SVGGraphicsElement[] {
    return d3Select(destinationColoredYarnSetElement).selectAll(`.${this.labelForProductionSchedulePath}`).nodes() as SVGGraphicsElement[];
  }

  private getTargetPositionOfDraggedProductionSchedulePathInColoredYarnSetPath(event: DragEvent, destinationPathElement: SVGGraphicsElement): number {
    const mouseCoordinatesInProductionSchedulePlanView: Coordinates = this.getMouseCoordinatesInProductionSchedulePlanView(event);

    const destinationPathElementSelection = d3Select(destinationPathElement);
    const productionSchedulePathPositionStart = Number(destinationPathElementSelection.attr('x'));
    const productionSchedulePathPositionEnd = sum([productionSchedulePathPositionStart, Number(destinationPathElementSelection.attr('width'))]);
    const productionSchedulePathDropThreshold = (productionSchedulePathPositionEnd - productionSchedulePathPositionStart) / 2;

    const isInLeftSideOfPath: boolean = inRange(
      mouseCoordinatesInProductionSchedulePlanView.x,
      productionSchedulePathPositionStart,
      productionSchedulePathPositionStart + productionSchedulePathDropThreshold
    );
    return isInLeftSideOfPath ? productionSchedulePathPositionStart : productionSchedulePathPositionEnd;
  }

  private drawProductionSchedulePathDropIndicator(parentElement: Element, dropIndicatorPosition: number): void {
    d3Select(parentElement).append('rect').attr('class', 'production-schedule-drop-indicator vertical').attr('x', dropIndicatorPosition).raise();
  }

  private drawProductionScheduleItemInPathGroupDropIndicator(element: Element, dropIndicatorPosition: number): void {
    d3Select(element).append('rect').attr('class', 'production-schedule-drop-indicator horizontal').attr('y', dropIndicatorPosition).raise();
  }

  private onDragEndProductionSchedulePath(event: DragEvent): void {
    if (this.isEditPathsEnabled) {
      const {x: mouseCoordinateX, y: mouseCoordinateY} = this.getMouseCoordinatesInProductionSchedulePlanView(event);

      const hasDraggedFarEnoughFromStartingPoint: boolean = isUndefined(this.mouseCoordinatesOfDragStart)
        ? false
        : isCoordinateOutOfRangeOfOtherCoordinate(
            {
              x: mouseCoordinateX,
              y: mouseCoordinateY
            },
            this.mouseCoordinatesOfDragStart
          );

      if (hasDraggedFarEnoughFromStartingPoint && !isUndefined(this.destinationOfDraggedProductionSchedulePath)) {
        const destinationOfDraggedProductionSchedulePathElement: SVGGraphicsElement = this.destinationOfDraggedProductionSchedulePath.node() as SVGGraphicsElement;
        const destinationOfDraggedProductionSchedulePathClassList = destinationOfDraggedProductionSchedulePathElement.classList;

        if (destinationOfDraggedProductionSchedulePathClassList.contains(this.labelForProductionSchedulePath)) {
          this.handleDropOnProductionSchedulePath(event);
        } else if (destinationOfDraggedProductionSchedulePathClassList.contains(this.labelForProductionScheduleColoredYarnSet)) {
          this.handleDropOnProductionScheduleColoredYarnSet();
        }
      }
    }

    this.cleanProductionSchedulePlanAfterDrop();
  }

  private handleDropOnProductionSchedulePath(event: DragEvent): void {
    let destinationOfDraggedProductionSchedulePath: ProductionSchedulePath = this.destinationOfDraggedProductionSchedulePath.datum() as ProductionSchedulePath;

    if (isUndefined(destinationOfDraggedProductionSchedulePath)) {
      destinationOfDraggedProductionSchedulePath = this.productionSchedulePath;
    }

    const targetIndexOfDraggedProductionSchedulePath: number = this.getTargetIndexOfDestinationProductionSchedulePath(
      event,
      this.destinationOfDraggedProductionSchedulePath.node() as SVGGraphicsElement
    );
    this.moveDraggedProductionSchedulePathToProductionScheduleColoredYarnSet(
      this.productionSchedule.getProductionScheduleColoredYarnSetForProductionSchedulePath(destinationOfDraggedProductionSchedulePath),
      targetIndexOfDraggedProductionSchedulePath
    );
  }

  private handleDropOnProductionScheduleColoredYarnSet(): void {
    const destinationOfDraggedProductionSchedulePath: OverviewListColoredYarnSetWithStartDent = this.destinationOfDraggedProductionSchedulePath.datum() as OverviewListColoredYarnSetWithStartDent;
    this.moveDraggedProductionSchedulePathToProductionScheduleColoredYarnSet(destinationOfDraggedProductionSchedulePath);
  }

  private moveDraggedProductionSchedulePathToProductionScheduleColoredYarnSet(
    destinationProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent,
    targetIndexOfDraggedProductionSchedulePath?: number
  ): void {
    const canMoveProductionSchedulePathToColoredYarnSet: boolean = this.productionSchedule.canMoveProductionSchedulePathToColoredYarnSet(
      this.productionSchedulePath.pathNumber,
      this.productionSchedule.getProductionScheduleColoredYarnSetForProductionSchedulePath(this.productionSchedulePath).startDent,
      destinationProductionScheduleColoredYarnSet.startDent
    );

    if (canMoveProductionSchedulePathToColoredYarnSet) {
      this.productionSchedulePlan.moveProductionSchedulePathToColoredYarnSet(
        this.productionSchedulePath,
        this.productionSchedule.getProductionScheduleColoredYarnSetForProductionSchedulePath(this.productionSchedulePath),
        destinationProductionScheduleColoredYarnSet,
        targetIndexOfDraggedProductionSchedulePath
      );
    }
  }

  private getProductionSchedulePathPosition(): number {
    let result = this.productionSchedulePath.startDent - 1;

    if (this.canRepositionProductionSchedulePathBeforeVerification()) {
      result = sumBy(this.productionSchedulePathsOfColoredYarnSet.productionSchedulePaths.slice(0, this.productionSchedulePathIndex), 'technicalWidthInDents');
    } else if (this.canRepositionProductionSchedulePathAfterVerification()) {
      const totalWidthInDentsOfPreviousColoredYarnSets = sumBy(this.productionSchedule.coloredYarnSets.slice(0, this.productionScheduleColoredYarnSetIndex), 'technicalWidthInDents');
      result -= totalWidthInDentsOfPreviousColoredYarnSets;
    }

    return multiply(result, this.aspectRatio.width);
  }

  private removeProductionSchedulePathFromProductionSchedulePathsOfColoredYarnSet(): void {
    const productionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(
      this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
    );
    this.productionSchedulePlan.removeProductionSchedulePath(this.productionSchedulePath.pathNumber, productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent);
  }

  private canRepositionProductionSchedulePathBeforeVerification(): boolean {
    return (
      !isEqual(this.productionSchedulePhase, ProductionSchedulePhase.VERIFY) &&
      (isEqual(this.productionSchedulePathIndex, 0) || size(this.productionSchedulePathsOfColoredYarnSet.productionSchedulePaths) > 1)
    );
  }

  private canRepositionProductionSchedulePathAfterVerification(): boolean {
    return (
      isEqual(this.productionSchedulePhase, ProductionSchedulePhase.VERIFY) &&
      size(this.productionSchedule.productionSchedulePathsOfColoredYarnSets) > 1 &&
      !isEqual(this.productionScheduleColoredYarnSetIndex, 0)
    );
  }

  private deleteProductionSchedulePath(): void {
    if (!isEmpty(this.productionSchedulePath.pathGroups)) {
      this.dialogBuilderFactoryService
        .getBuilder()
        .openAlertDialog({
          titleText: 'GENERAL.ACTIONS.DELETE',
          messageText: this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.DELETE_PRODUCTION_ORDER_PATH'),
          type: DialogType.DELETE
        })
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((hasConfirmed: AlertDialogResult) => {
          if (isEqual(hasConfirmed, AlertDialogResult.CONFIRM)) {
            this.removeProductionSchedulePathFromProductionSchedulePathsOfColoredYarnSet();
          }
        });
    } else {
      this.removeProductionSchedulePathFromProductionSchedulePathsOfColoredYarnSet();
    }
  }

  private getMouseCoordinatesInProductionSchedulePath(event: DragEvent): Coordinates {
    const productionScheduleWrapperElement = d3Select(this.elementRef.nativeElement).node() as ContainerElement;
    const [mouseCoordinateX, mouseCoordinateY] = d3Pointer(event, productionScheduleWrapperElement);

    return {x: mouseCoordinateX, y: mouseCoordinateY};
  }

  private getMouseCoordinatesInProductionSchedulePlanView(event: DragEvent): Coordinates {
    const productionScheduleWrapperElement = d3Select(`.${this.labelForProductionScheduleBuilderGroup}`).node() as ContainerElement;
    const [mouseCoordinateX, mouseCoordinateY] = d3Pointer(event, productionScheduleWrapperElement);
    return {x: mouseCoordinateX, y: mouseCoordinateY};
  }

  private getOrderlineProperty(orderLineId: number): ProductionScheduleOrderLine {
    if (orderLineId !== null && this.productionSchedule && !AssertionUtils.isEmpty(this.productionSchedule.orderLines)) {
      const foundOrderLine = this.productionSchedule.orderLines.find((orderLine: ProductionScheduleOrderLine) => orderLine.id === orderLineId);

      if (foundOrderLine) {
        return foundOrderLine;
      }
    }

    return null;
  }
}
